import { z } from 'zod';
/**
 * Correo de la entidad (GUIA-AGENTES.md §3.10 de la suite): con qué servicio y a nombre de quién le escriben las apps
 * al ciudadano y al personal. En la suite, un superadministrador registra uno o varios correos de la entidad y le asigna
 * uno a cada app (fuentes, §3.12); cada app lee el suyo por la API de servicio. Desacoplada, la app lo guarda ella misma
 * con estos mismos esquemas.
 * Los secretos (contraseña, secreto del cliente, llave de la cuenta de servicio) se guardan cifrados y nunca vuelven
 * a la web. Sin el campo del secreto se conserva el guardado (si el destino no cambió); `null` lo quita.
 *
 * Esta entrada no usa nada de Node (sirve en la web); los transportes están en `@xiltrion/correo/transportes`.
 */
export const EmailProvider = {
    SMTP: 'SMTP',
    MICROSOFT_365: 'MICROSOFT_365',
    GOOGLE_WORKSPACE: 'GOOGLE_WORKSPACE',
};
export const EMAIL_PROVIDER_LABEL = {
    SMTP: 'Servidor SMTP',
    MICROSOFT_365: 'Microsoft 365',
    GOOGLE_WORKSPACE: 'Google Workspace',
};
export const SMTP_SECURITY = ['tls', 'starttls', 'none'];
/** Servicios conocidos: llenan servidor, puerto y seguridad. «custom» deja todo a mano. */
export const SMTP_PRESETS = {
    custom: { label: 'Otro servidor', host: '', port: 587, security: 'starttls', hint: 'Los datos los da quien administra el servidor de correo de la entidad.' },
    gmail: { label: 'Gmail / Google Workspace (contraseña de aplicación)', host: 'smtp.gmail.com', port: 465, security: 'tls', hint: 'Usuario: la cuenta de correo. Contraseña: una contraseña de aplicación (exige verificación en dos pasos). Para Workspace es mejor la opción Google Workspace.' },
    office365: { label: 'Outlook / Microsoft 365 (SMTP AUTH)', host: 'smtp.office365.com', port: 587, security: 'starttls', hint: 'Microsoft retiró el usuario y contraseña por SMTP en Exchange Online: use la opción Microsoft 365.' },
    zoho: { label: 'Zoho Mail', host: 'smtp.zoho.com', port: 465, security: 'tls', hint: 'Usuario: la cuenta de correo. Contraseña: una contraseña específica de aplicación. Cuentas en Europa: smtp.zoho.eu.' },
    ses: { label: 'Amazon SES', host: 'email-smtp.us-east-1.amazonaws.com', port: 587, security: 'starttls', hint: 'Credenciales SMTP de SES (no las llaves de IAM). Cambie la región del servidor si no es us-east-1.' },
    sendgrid: { label: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, security: 'starttls', user: 'apikey', hint: 'Usuario: apikey. Contraseña: una llave de API con permiso de envío.' },
    mailgun: { label: 'Mailgun', host: 'smtp.mailgun.org', port: 587, security: 'starttls', hint: 'Credenciales SMTP del dominio en Mailgun. Cuentas en Europa: smtp.eu.mailgun.org.' },
    brevo: { label: 'Brevo', host: 'smtp-relay.brevo.com', port: 587, security: 'starttls', hint: 'Usuario: el login SMTP de Brevo. Contraseña: una llave SMTP.' },
    postmark: { label: 'Postmark', host: 'smtp.postmarkapp.com', port: 587, security: 'starttls', hint: 'Usuario y contraseña: el token del servidor de Postmark.' },
};
const PRESET_KEYS = Object.keys(SMTP_PRESETS);
const email = z.string().trim().toLowerCase().email('Correo no válido').max(320);
const optionalText = (max) => z.string().trim().max(max).optional().nullable();
const secret = (max) => z.string().max(max).optional().nullable();
/** Remitente: el nombre por defecto es el de la entidad; «responder a» por defecto es el buzón de entrada. */
const sender = {
    fromName: optionalText(200),
    fromAddress: email.optional().nullable(),
    replyTo: email.optional().nullable().or(z.literal('').transform(() => null)),
};
export const smtpEmailSchema = z.object({
    provider: z.literal('SMTP'),
    preset: z.enum(PRESET_KEYS).default('custom'),
    host: z.string().trim().min(1, 'Indica el servidor').max(255).regex(/^[a-zA-Z0-9.-]+$/, 'Servidor no válido'),
    port: z.coerce.number().int().min(1).max(65535),
    security: z.enum(SMTP_SECURITY),
    user: optionalText(320),
    password: secret(500),
    ...sender,
    fromAddress: email,
});
export const microsoft365EmailSchema = z.object({
    provider: z.literal('MICROSOFT_365'),
    /** Id del directorio (inquilino) de Microsoft Entra. */
    tenantId: z.string().trim().min(1, 'Indica el id del directorio').max(100).regex(/^[a-zA-Z0-9.-]+$/, 'Id del directorio no válido'),
    /** Id de la aplicación registrada. */
    clientId: z.string().trim().uuid('El id de la aplicación es un GUID'),
    clientSecret: secret(500),
    /** Buzón desde el que se envía. */
    sender: email,
    ...sender,
});
export const googleWorkspaceEmailSchema = z.object({
    provider: z.literal('GOOGLE_WORKSPACE'),
    serviceAccountEmail: email,
    /** Llave privada (PEM) de la cuenta de servicio. */
    privateKey: secret(10_000).refine((k) => !k || /-----BEGIN (RSA )?PRIVATE KEY-----/.test(k), 'La llave debe ser la «private_key» del JSON de la cuenta de servicio'),
    /** Usuario de Workspace en cuyo nombre se envía (delegación de todo el dominio). */
    sender: email,
    ...sender,
});
export const outboundEmailSchema = z.discriminatedUnion('provider', [smtpEmailSchema, microsoft365EmailSchema, googleWorkspaceEmailSchema]);
/** El campo secreto de cada proveedor. */
export const EMAIL_SECRET_FIELD = { SMTP: 'password', MICROSOFT_365: 'clientSecret', GOOGLE_WORKSPACE: 'privateKey' };
/** Separa el secreto de lo que se puede mostrar. */
export function splitSecret(input) {
    const field = EMAIL_SECRET_FIELD[input.provider];
    const { [field]: secret, ...settings } = input;
    return { settings: settings, secret: secret };
}
export function joinSecret(settings, secret) {
    return { ...settings, [EMAIL_SECRET_FIELD[settings.provider]]: secret };
}
/**
 * A dónde se le entrega el secreto. El guardado solo se reutiliza si esto no cambió, para que nadie lo envíe a otro
 * servidor cambiando solo la dirección.
 */
export function outboundEmailTarget(c) {
    switch (c.provider) {
        case 'SMTP': return `smtp:${c.host.toLowerCase()}:${(c.user ?? '').toLowerCase()}`;
        case 'MICROSOFT_365': return `m365:${c.tenantId.toLowerCase()}:${c.clientId.toLowerCase()}`;
        case 'GOOGLE_WORKSPACE': return `google:${c.serviceAccountEmail.toLowerCase()}`;
    }
}
/** Dirección del remitente: la indicada, o el buzón que envía, o el usuario SMTP si es un correo. */
export function outboundEmailFrom(c) {
    if (c.fromAddress)
        return c.fromAddress;
    if (c.provider === 'SMTP')
        return c.user && c.user.includes('@') ? c.user : null;
    return c.sender;
}
/** Probar: con el formulario (o, sin él, con lo guardado) y, si se pide, enviando un correo al administrador. */
export const testOutboundEmailSchema = z.object({
    config: outboundEmailSchema.optional(),
    sendTest: z.boolean().default(false),
});
/**
 * Estados del correo certificado (Datacrédito Postal), en orden de avance. `sin_consultar` es nuestro: el envío ya
 * se hizo y todavía no se preguntó su estado.
 */
export const CERTIFIED_STATE_LABEL = {
    sin_consultar: 'Pendiente de confirmar',
    en_cola: 'En cola',
    programado: 'Programado',
    reintentando: 'Reintentando',
    enviado: 'Enviado',
    entregado: 'Entregado',
    abierto: 'Abierto',
    leido: 'Leído',
    no_entregado: 'No entregado',
    error: 'Error',
    cancelado: 'Cancelado',
};
const CERTIFIED_ORDER = ['sin_consultar', 'en_cola', 'programado', 'reintentando', 'enviado', 'entregado', 'abierto', 'leido'];
const CERTIFIED_PROBLEM = ['no_entregado', 'error', 'cancelado'];
/** El estado como lo manda la API, normalizado (`Entregado` → `entregado`, `no entregado` → `no_entregado`). */
export function certifiedEstado(raw) {
    return String(raw ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}
/** Estados que ya no cambian: no hay que volver a preguntarlos. */
export const CERTIFIED_FINAL_STATES = ['leido', 'no_entregado', 'error', 'cancelado'];
/** Estados en los que la API ya tiene acta (entrega, rebote o lectura). */
export const CERTIFIED_ACTA_STATES = ['entregado', 'abierto', 'leido', 'no_entregado'];
/**
 * ¿El estado nuevo puede reemplazar al actual? Un evento que llega tarde (p. ej. «enviado» después de «entregado») no
 * retrocede el estado; un problema siempre se registra, salvo que el envío ya esté en un estado final.
 */
export function certifiedAdvances(actual, nuevo) {
    const from = actual ?? 'sin_consultar';
    if (CERTIFIED_FINAL_STATES.includes(from))
        return false;
    // Un estado que no conocemos se muestra tal cual mientras no haya uno conocido: nunca se descarta en silencio.
    const conocido = (e) => CERTIFIED_ORDER.includes(e) || CERTIFIED_PROBLEM.includes(e);
    if (!conocido(nuevo))
        return nuevo !== from && (from === 'sin_consultar' || !conocido(from));
    if (CERTIFIED_PROBLEM.includes(nuevo))
        return true;
    return CERTIFIED_ORDER.indexOf(nuevo) > CERTIFIED_ORDER.indexOf(from);
}
/** Del `providerResult` de un envío: `trackingIds` (dirección → tracking_id) y `tracking` (estado por dirección). */
export function certifiedRastreo(pr) {
    const emails = Object.keys(pr?.trackingIds ?? {});
    if (!emails.length)
        return null;
    const estados = emails.map((e) => pr?.tracking?.[e]?.estado ?? 'sin_consultar');
    const problem = estados.find((e) => CERTIFIED_PROBLEM.includes(e));
    const least = estados.reduce((a, b) => (CERTIFIED_ORDER.indexOf(b) < CERTIFIED_ORDER.indexOf(a) ? b : a));
    return {
        estado: problem ?? least,
        total: emails.length,
        entregados: estados.filter((e) => ['entregado', 'abierto', 'leido'].includes(e)).length,
    };
}
/**
 * Certificado de comunicaciones (Datacrédito Postal). Es aparte del correo saliente: aquí solo va la credencial. Qué
 * se certifica lo decide cada paso del flujo que escribe al ciudadano (`config.certified`, ver `CERTIFIABLE_ACTIONS`).
 * Recuperar contraseña, los códigos de firma y los avisos a funcionarios no pasan por aquí. La credencial son dos datos: el `client_id` (va en cada
 * petición) y el `client_secret` (solo firma; nunca se envía ni vuelve a la web). No se escriben juntos.
 */
const CERTIFIED_CLIENT_ID = /^[A-Za-z0-9_-]+$/;
export const certifiedEmailSchema = z.object({
    /** Base de la API (con su `/api`). */
    endpoint: z.string().trim().url('Indica la dirección del servicio').max(500)
        .refine((u) => u.startsWith('https://'), 'La dirección debe ser HTTPS')
        .refine((u) => !u.includes('?') && !u.includes('#'), 'La dirección no lleva parámetros'),
    clientId: z.string().trim().min(1, 'Indica el client_id').max(100).regex(CERTIFIED_CLIENT_ID, 'El client_id lleva solo letras, números, guion y guion bajo'),
    clientSecret: secret(200).refine((k) => !k || !/\s/.test(k), 'El client_secret va sin espacios'),
}).refine((c) => !c.clientSecret || !c.clientSecret.startsWith(`${c.clientId}:`), {
    path: ['clientSecret'], message: 'Escriba solo el client_secret, sin el client_id delante',
});
export const CERTIFIED_EMAIL_SECRET_FIELD = 'clientSecret';
/** A quién se le entrega el secreto: si la dirección o el client_id cambian, el guardado no se reutiliza. */
export function certifiedEmailTarget(c) {
    return `certified:${c.endpoint.toLowerCase()}:${c.clientId}`;
}
export const testCertifiedEmailSchema = z.object({ config: certifiedEmailSchema.optional() });
// ─────────────────────────────────────────── Contrato con la suite
/**
 * `GET /api/v1/service/entities/:slug/mail` (con la clave de servicio de una app habilitada en la entidad): el correo
 * que la entidad le asignó a la app que llama (en la suite son fuentes, GUIA §3.12), completo y con sus secretos.
 * `null` = la app no tiene correo de ese tipo asignado y usa lo suyo. `updatedAt` cambia con cada cambio de esas
 * fuentes: la app lo usa como marca para renovar sus transportes.
 */
const certifiedForAppSchema = z.object({ endpoint: z.string(), clientId: z.string(), clientSecret: z.string() });
/**
 * Por dónde sale un envío de la app (`features` de `entityMailSchema`): un correo saliente, el certificado o nada
 * (`OFF`, la entidad lo apagó). Los envíos sin ruta salen por el principal (`outbound`).
 */
export const mailRouteSchema = z.discriminatedUnion('channel', [
    /** `name`: el nombre de la fuente en la suite («Notificaciones», «Certificado Datacrédito»), para mostrarlo. */
    z.object({ channel: z.literal('OUTBOUND'), name: z.string().nullish(), outbound: outboundEmailSchema }),
    z.object({ channel: z.literal('CERTIFIED'), name: z.string().nullish(), certified: certifiedForAppSchema }),
    z.object({ channel: z.literal('OFF') }),
]);
export const entityMailSchema = z.object({
    outbound: outboundEmailSchema.nullable(),
    certified: certifiedForAppSchema.nullable(),
    /** Nombre en la suite del correo principal (`outbound`), para mostrar por dónde sale. */
    outboundName: z.string().nullish(),
    /** Ruta de cada envío que la entidad eligió aparte (código del catálogo de la app → ruta). Ver `mailForFeature`. */
    features: z.record(z.string(), mailRouteSchema).default({}),
    updatedAt: z.string().nullable(),
});
/**
 * Por dónde sale el envío `feature` de la app: su ruta (la suite ya resolvió la herencia de `parent`), si no la de
 * `fallback` (el envío base, para un envío que la suite todavía no conoce), si no el correo principal. `null` = la
 * entidad no le asignó correo a la app (usa lo suyo). Con `OFF`, la app no lo envía.
 */
export function mailForFeature(mail, feature, fallback) {
    const route = mail.features?.[feature] ?? (fallback ? mail.features?.[fallback] : undefined);
    if (route)
        return route;
    return mail.outbound ? { channel: 'OUTBOUND', name: mail.outboundName ?? null, outbound: mail.outbound } : null;
}
/** Código de un envío: minúsculas, números, punto, guion y guion bajo (`radicacion.confirmacion`). */
export const MAIL_FEATURE_CODE = /^[a-z0-9][a-z0-9._-]*$/;
/** Un envío de la app (un tipo de correo que manda): la entidad elige por dónde sale cada uno. */
export const mailFeatureSchema = z.object({
    code: z.string().min(1).max(80).regex(MAIL_FEATURE_CODE),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(300).nullish(),
    /** Para ordenarlos en la consola (`Radicación`, `Tareas`). */
    group: z.string().trim().max(60).nullish(),
    /** Se puede mandar por correo certificado (la app sabe enviarlo así). */
    certifiable: z.boolean().default(false),
    /**
     * Envío base del que hereda la ruta si la entidad no le elige una (un paso de un flujo hereda de «Notificación de la
     * respuesta»). Debe ser un envío del catálogo fijo de la app.
     */
    parent: z.string().min(1).max(80).regex(MAIL_FEATURE_CODE).nullish(),
});
/**
 * La app publica su catálogo de envíos: el fijo, igual para todas las entidades (`PUT /api/v1/service/mail-features`),
 * y el de cada entidad (`PUT /api/v1/service/entities/:slug/mail-features`, p. ej. los pasos de sus flujos), cuyos
 * códigos no repiten los del fijo y cuyo `parent` es un envío del fijo.
 */
export const mailFeatureCatalogSchema = z.object({
    features: z.array(mailFeatureSchema).max(200).refine((f) => new Set(f.map((x) => x.code)).size === f.length, 'Hay códigos de envío repetidos'),
});
/**
 * `PUT /api/v1/service/entities/:slug/mail-routes`: traspaso único de lo que la app ya mandaba certificado. Cada envío
 * (de su catálogo publicado, certificable y sin ruta elegida) queda con ruta al correo certificado principal de la app.
 * Nunca pisa una ruta que la entidad ya eligió.
 */
export const mailRoutesImportSchema = z.object({
    routes: z.array(z.object({ feature: z.string().min(1).max(80), channel: z.literal('CERTIFIED') })).max(200),
});
/** `PUT /api/v1/service/entities/:slug/mail`: traspaso único desde una app que ya tenía el correo de la entidad. */
export const entityMailImportSchema = z.object({
    outbound: outboundEmailSchema.nullable().optional(),
    certified: certifiedEmailSchema.nullable().optional(),
});
//# sourceMappingURL=esquemas.js.map